<template>
    <form
        class="B2BForm"
        @submit.prevent="submit"
    >
        <div class=" row">
            <div class="col col-12 col-md-6">
                <InputFieldWithLabel 
                    v-model="firstName.value"
                    :label="$t('form-contact.firstName')"
                    :isRequired="true"
                    :errorLabel="getError(firstName.errors)"
                    data-cy="input.firstName"
                />
            </div>
            <div class="col col-12 col-md-6">
                <InputFieldWithLabel 
                    v-model="lastName.value"
                    :label="$t('form-contact.lastName')"
                    :isRequired="true"
                    :errorLabel="getError(lastName.errors)"
                    data-cy="input.lastName"
                />
            </div>
        </div>
        <div class="row">
            <div class="col col-12">
                <InputPhoneNumber
                    v-model="phoneNumber.value"
                    :country="country"
                    :lang="country"
                    required
                    data-cy="input.phoneNumber"
                    @update="updatePhoneNumber"
                    @valid="(isValid) => phoneNumber.isValid = isValid"
                />
                <div
                    v-if=" phoneNumber.errors && phoneNumber.errors.length > 0"
                    :aria-label="getError(phoneNumber.errors)"
                    :class="{ 'InputPhoneNumber-error--displayed': getError(phoneNumber.errors) !== '' }"
                    class="InputPhoneNumber-error"
                >
                    {{ getError(phoneNumber.errors) }}
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col col-12">
                <InputFieldWithLabel
                    v-model="email.value"
                    data-cy="input.email"
                    :isRequired="true"
                    :label="$t('form-contact.email')"
                    :errorLabel="getError(email.errors)"
                />
            </div>
        </div>
        <div class="row">
            <div class="col col-12">
                <InputFieldWithLabel
                    v-model="company.value"
                    data-cy="input.company"
                    :isRequired="true"
                    :label="$t('form-contact.company')"
                    :errorLabel="getError(company.errors)"
                />
            </div>
        </div>
        <div class="row">
            <div class="col col-12">
                <InputDropdown
                    v-model="fleetSize.value"
                    :label="$t('form-contact.fleet-size')"
                    :items="fleetSizes"
                    :isRequired="true"
                    data-cy="dropdown.fleetSize"
                    :errorLabel="getError(fleetSize.errors)"
                />
            </div>
        </div>
        <div class="row mb-small">
            <div class="col col-12">
                <InputFieldWithLabel
                    v-model="quantity.value"
                    inputType="number"
                    data-cy="input.quantity"
                    :isRequired="true"
                    :label="$t('form-contact.quantity')"
                    :errorLabel="getError(quantity.errors)"
                />
            </div>
        </div>
        <div class="row">
            <div class="col col-12">
                {{ $t('form-contact.fields-required') }}
            </div>
        </div>
        <div class="row">
            <div class="col col-12">
                <CheckBoxWithLabel
                    v-model="terms.value"
                    :label="$t('form-contact.terms')"
                />
            </div>
        </div>
        <div class="row">
            <div class="col col-12 col-md-8 offset-md-2 B2BForm-containerValidateButton">
                <RoundedButton
                    :label="$t('form-contact.validate')"
                    class="B2BForm-submitButton"
                    data-cy="button.validate"
                    type="reversed"
                    :loading="loading"
                    @buttonClicked="clickValidate"
                />
            </div>
        </div>
        <div class="row">
            <div class="col col-12 col-md-10 offset-md-1 B2BForm-containerDataConsent">
                <span
                    class="B2BForm-DataConsent"
                    v-html="$t('form-contact.data-consent')"
                />
            </div>
        </div>
    </form>
</template>

<script type="ts">
import Valid from '@/assets/images/valid.svg';

import Vue from "vue";
import Component from "vue-class-component";
import { InputFieldWithLabel, InputPhoneNumber } from '@free2move/vuejs-components'
import {required, emailFormat, validate} from "@free2move/vuejs-components/validators.js";

import RoundedButton from "@/components/common/RoundedButton.vue";
import CheckBoxWithLabel from '@/components/CheckBoxWithLabel.vue'
import InputDropdown from "@/components/common/InputDropdown.vue";

import {EVENT_NAME_FORM_BTN} from '@/constants/ga-event';

const B2BFormProps = Vue.extend({
    props: {
        country: {
            type: String,
            required: true
        },
        language: {
            type: String,
            required: true
        },
        user: {
            type: [Boolean, Object],
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    watch: {
        user(newUser) {
            if (!newUser) return false;
            const {
                firstName,
                lastName,
                phoneNumber,
                email
            } = newUser;

            this.firstName.value = firstName;
            this.lastName.value = lastName;
            this.phoneNumber.value = phoneNumber;
            this.email.value = email;
            this.firstName.value = firstName;
            this.company.value = newUser.company?.name || '';

        }
    }
})

@Component({
    components: {
        CheckBoxWithLabel,
        RoundedButton,
        InputFieldWithLabel, 
        InputPhoneNumber,
        InputDropdown,
        Valid
    },
})
export default class B2BForm extends B2BFormProps {
    data() {
        return {
            firstName: {
                value: '',
                validators: [required],
                errors: []
            },
            lastName: {
                value: '',
                validators: [required],
                errors: []
            },
            email: {
                value: '',
                validators: [required, emailFormat],
                errors: []
            },
            formattedPhoneNumber: '',
            phoneNumber: {
                value: '',
                validators: [required],
                errors: [],
            },
            company: {
                value: '',
                validators: [required],
                errors: []
            },
            quantity: {
                value: '',
                validators: [required],
                errors: []
            },
            fleetSize: {
                value: '',
                validators: [required],
                errors: []
            },
            terms: {
                value: true,
                validators: [],
                errors: []
            }
        };
    }

    clickValidate () {
        this.resetErrors();
        validate({
            firstName: this.firstName,
            lastName: this.lastName,
            email: this.email,
            company: this.company,
            quantity: this.quantity,
            phoneNumber: this.phoneNumber,
            fleetSize: this.fleetSize
        }).then(() => {
            this.$emit('push', {
                event_name: EVENT_NAME_FORM_BTN,
            });
          
            this.$emit('submit', {
                firstName: this.firstName.value,
                lastName: this.lastName.value,
                email: this.email.value,
                company: this.company.value,
                quantity: this.quantity.value,
                phoneNumber: this.formattedPhoneNumber,
                fleetSize: this.fleetSizes[this.fleetSize.value],
                terms: this.terms.value
            })
        }).catch((errors) => {
            const {
                firstName: firstNameErrors = [],
                lastName: lastNameErrors = [],
                email: emailErrors = [],
                company: companyErrors = [],
                quantity: quantityErrors = [],
                phoneNumber: phoneNumberErrors = [],
                fleetSize: fleetSizeErrors = [],
            } = errors;

            this.firstName.errors = firstNameErrors;
            this.lastName.errors = lastNameErrors;
            this.email.errors = emailErrors;
            this.company.errors = companyErrors;
            this.quantity.errors = quantityErrors;
            this.phoneNumber.errors = phoneNumberErrors;
            this.fleetSize.errors = fleetSizeErrors;
        });
    }
    getError(errors) {
        return errors?.length > 0 ? this.$t('form-contact.error.' + errors[0]) : ''
    }
    resetErrors() {
        this.firstName.errors = [];
        this.lastName.errors = [];
        this.email.errors = [];
        this.company.errors = [];
        this.quantity.errors = [];
        this.terms.errors = [];
    }

    updatePhoneNumber(value) {
        this.formattedPhoneNumber = value.formattedNumber;
    }

    get fleetSizes() {
        return {
            '0': this.$t('form-contact.sizes.0'),
            '1': this.$t('form-contact.sizes.1'),
            '2': this.$t('form-contact.sizes.2'),
        }
    }

}
</script>

<style lang="scss" scoped>
.B2BForm{
    color: #573781;
    padding: 8px;

    &-containerValidateButton, &-containerDataConsent {
      text-align: center;
    }

    &-containerBlock{
        margin-top: 60px;
        max-width: 495px;

        @media (max-width: $f2m-desktop-breakpoint) {
            max-width: 100%;
        }
    }

    &-inputContainer{
        margin-bottom: 28px;
    }

    &-submitButton{
        width: 100%;
        font-size: 14px;
    }

    &-containerSent{
      text-align: center;
      padding: 8px;

      &-title{
        font-size: 22px;
        font-weight: 800;
        margin-bottom: 12px;
      }

      &-subtitle{
        font-size: 18px;
        font-weight: 400;
      }
      &-icon{
        margin-bottom: 24px;
      }

    }

    &-DataConsent{
      font-size: 12px;
      font-weight: 400;
    }

    .row{
      &.mb-small .col {
        margin-bottom: 12px;
      }
      .col{
          margin-bottom: 24px;
        }
    }
}

</style>